import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";
import { FamilyWeekend, Team, Game, Profile } from "@/types";

/**
 * Admin info type
 */
interface WeekendAdmin {
  id: string;
  profile: Pick<Profile, "id" | "full_name" | "avatar_url">;
}

/**
 * Team score card component
 */
interface TeamScoreCardProps {
  team: Team;
  rank: number;
}

const TeamScoreCard = ({ team, rank }: TeamScoreCardProps) => {
  const colors = useThemeColors();
  const medalEmoji = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;

  return (
    <View
      className="rounded-xl p-4 mb-3 flex-row items-center"
      style={{
        backgroundColor: colors.surface,
        borderWidth: rank === 1 ? 2 : 1,
        borderColor: rank === 1 ? "#FBBF24" : colors.border,
      }}
    >
      <View className="w-10 items-center mr-3">
        {medalEmoji ? (
          <Text className="text-2xl">{medalEmoji}</Text>
        ) : (
          <Text className="text-lg font-bold" style={{ color: colors.textTertiary }}>
            #{rank}
          </Text>
        )}
      </View>
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: team.color ? `${team.color}20` : colors.primaryLight }}
      >
        <Text className="font-bold" style={{ color: team.color || colors.primary }}>
          {team.name.charAt(0).toUpperCase()}
        </Text>
      </View>
      <View className="flex-1">
        <Text className="font-semibold" style={{ color: colors.text }}>
          {team.name}
        </Text>
        {team.description && (
          <Text className="text-sm" numberOfLines={1} style={{ color: colors.textSecondary }}>
            {team.description}
          </Text>
        )}
      </View>
      <View className="items-end">
        <Text className="text-xl font-bold" style={{ color: colors.primary }}>
          {team.total_score}
        </Text>
        <Text className="text-xs" style={{ color: colors.textTertiary }}>punten</Text>
      </View>
    </View>
  );
};

/**
 * Weekend detail screen - View historical weekend data
 */
export default function WeekendDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const colors = useThemeColors();

  const [weekend, setWeekend] = useState<FamilyWeekend | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [admins, setAdmins] = useState<WeekendAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch weekend details, teams, games, and admins
   */
  const fetchWeekendData = useCallback(async () => {
    if (!id) return;

    try {
      // Fetch weekend
      const { data: weekendData, error: weekendError } = await supabase
        .from("family_weekends")
        .select("*")
        .eq("id", id)
        .single();

      if (weekendError) {
        console.error("Error fetching weekend:", weekendError);
        router.back();
        return;
      }

      setWeekend(weekendData);

      // Fetch teams for this weekend
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select("*")
        .eq("weekend_id", id)
        .order("total_score", { ascending: false });

      if (!teamsError && teamsData) {
        setTeams(teamsData);
      }

      // Fetch games for this weekend (will be empty until games feature is built)
      const { data: gamesData, error: gamesError } = await supabase
        .from("games")
        .select("*")
        .eq("weekend_id", id)
        .order("created_at", { ascending: true });

      if (!gamesError && gamesData) {
        setGames(gamesData);
      }

      // Fetch familie_admin roles for this weekend (historical admins)
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select(`
          id,
          profiles:user_id (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq("weekend_id", id)
        .eq("role", "familie_admin");

      if (!rolesError && rolesData) {
        // Filter out any null profiles and type correctly
        const validAdmins = rolesData
          .filter((r: any) => r.profiles)
          .map((r: any) => ({
            id: r.id,
            profile: r.profiles,
          }));
        setAdmins(validAdmins);
      }
    } catch (error) {
      console.error("Error fetching weekend data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  useEffect(() => {
    fetchWeekendData();
  }, [fetchWeekendData]);

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Weekend laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!weekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        <Ionicons name="calendar-outline" size={48} color={colors.border} />
        <Text className="mt-2" style={{ color: colors.textTertiary }}>
          Weekend niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "bottom"]}
    >
      {/* Header */}
      <View
        className="px-4 py-4 flex-row items-center"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <Pressable
          onPress={() => router.back()}
          className="mr-3"
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View className="flex-1">
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            {weekend.name}
          </Text>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            {weekend.year}
          </Text>
        </View>
        {weekend.is_active && (
          <View className="bg-green-100 px-3 py-1 rounded-full">
            <Text className="text-green-700 text-sm font-medium">Actief</Text>
          </View>
        )}
      </View>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* Weekend Info Card */}
        <View
          className="mx-4 mt-4 rounded-xl p-4"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          {weekend.theme && (
            <View className="flex-row items-center mb-3">
              <Ionicons name="color-palette-outline" size={18} color={colors.primary} />
              <Text className="ml-2" style={{ color: colors.textSecondary }}>
                Thema: {weekend.theme}
              </Text>
            </View>
          )}
          {weekend.location_name && (
            <View className="flex-row items-center mb-3">
              <Ionicons name="location-outline" size={18} color={colors.primary} />
              <Text className="ml-2" style={{ color: colors.textSecondary }}>
                {weekend.location_name}
              </Text>
            </View>
          )}
          {weekend.start_date && weekend.end_date && (
            <View className="flex-row items-center">
              <Ionicons name="calendar-outline" size={18} color={colors.primary} />
              <Text className="ml-2" style={{ color: colors.textSecondary }}>
                {new Date(weekend.start_date).toLocaleDateString("nl-NL", {
                  day: "numeric",
                  month: "long",
                })}
                {" - "}
                {new Date(weekend.end_date).toLocaleDateString("nl-NL", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Text>
            </View>
          )}
        </View>

        {/* Organisatie Section - Shows who organized this weekend */}
        {admins.length > 0 && (
          <View className="px-4 mt-6">
            <Text
              className="text-sm font-semibold uppercase mb-3"
              style={{ color: colors.textTertiary }}
            >
              👑 Organisatie
            </Text>
            <View
              className="rounded-xl overflow-hidden"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              {admins.map((admin, index) => (
                <View
                  key={admin.id}
                  className="flex-row items-center p-4"
                  style={{
                    borderBottomWidth: index < admins.length - 1 ? 1 : 0,
                    borderBottomColor: colors.border,
                  }}
                >
                  <View className="w-10 h-10 rounded-full bg-amber-100 items-center justify-center mr-3">
                    <Text className="text-lg">
                      {admin.profile.full_name?.charAt(0).toUpperCase() || "👤"}
                    </Text>
                  </View>
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      {admin.profile.full_name || "Onbekend"}
                    </Text>
                    <Text className="text-sm text-amber-600">Familie Admin</Text>
                  </View>
                  <Ionicons name="shield-checkmark" size={20} color="#D97706" />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Leaderboard Section */}
        <View className="px-4 mt-6">
          <Text
            className="text-sm font-semibold uppercase mb-3"
            style={{ color: colors.textTertiary }}
          >
            🏆 Eindstand
          </Text>

          {teams.length > 0 ? (
            teams.map((team, index) => (
              <TeamScoreCard key={team.id} team={team} rank={index + 1} />
            ))
          ) : (
            <View
              className="rounded-xl p-6 items-center"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              <Ionicons name="people-outline" size={48} color={colors.border} />
              <Text className="mt-2" style={{ color: colors.textTertiary }}>
                Geen teams gevonden
              </Text>
            </View>
          )}
        </View>

        {/* Quick Stats */}
        {teams.length > 0 && (
          <View className="px-4 mt-6">
            <Text
              className="text-sm font-semibold uppercase mb-3"
              style={{ color: colors.textTertiary }}
            >
              📊 Statistieken
            </Text>
            <View className="flex-row">
              <View
                className="flex-1 rounded-xl p-4 mr-2 items-center"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <Text className="text-2xl font-bold" style={{ color: colors.primary }}>
                  {teams.length}
                </Text>
                <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  Teams
                </Text>
              </View>
              <View
                className="flex-1 rounded-xl p-4 ml-2 items-center"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <Text className="text-2xl font-bold" style={{ color: colors.primary }}>
                  {teams.reduce((sum, t) => sum + t.total_score, 0)}
                </Text>
                <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  Totaal punten
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Games Section */}
        <View className="px-4 mt-6">
          <Text
            className="text-sm font-semibold uppercase mb-3"
            style={{ color: colors.textTertiary }}
          >
            🎮 Spellen
          </Text>

          {games.length > 0 ? (
            <View
              className="rounded-xl overflow-hidden"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              {games.map((game, index) => (
                <View
                  key={game.id}
                  className="p-4"
                  style={{
                    borderBottomWidth: index < games.length - 1 ? 1 : 0,
                    borderBottomColor: colors.border,
                  }}
                >
                  <View className="flex-row items-center">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: colors.primaryLight }}
                    >
                      <Ionicons
                        name={
                          game.type === "quiz"
                            ? "help-circle-outline"
                            : game.type === "speurtocht"
                            ? "map-outline"
                            : game.type === "challenge"
                            ? "trophy-outline"
                            : game.type === "photo_hunt"
                            ? "camera-outline"
                            : "game-controller-outline"
                        }
                        size={20}
                        color={colors.primary}
                      />
                    </View>
                    <View className="flex-1">
                      <Text className="font-medium" style={{ color: colors.text }}>
                        {game.name}
                      </Text>
                      <Text className="text-sm capitalize" style={{ color: colors.textSecondary }}>
                        {game.type.replace("_", " ")}
                      </Text>
                    </View>
                    {game.status === "completed" && (
                      <View className="bg-green-100 px-2 py-1 rounded-full">
                        <Text className="text-xs text-green-700 font-medium">Voltooid</Text>
                      </View>
                    )}
                  </View>
                  {game.description && (
                    <Text
                      className="text-sm mt-2 ml-13"
                      numberOfLines={2}
                      style={{ color: colors.textSecondary }}
                    >
                      {game.description}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          ) : (
            <View
              className="rounded-xl p-6 items-center"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              <Ionicons name="game-controller-outline" size={48} color={colors.border} />
              <Text className="mt-2" style={{ color: colors.textTertiary }}>
                Geen spellen gevonden
              </Text>
            </View>
          )}
        </View>

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}
